import { useEffect, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import { ToastType } from '@components/toast/ToastType.enum';
import { useCreateQuoteRating } from '@services/quoteRating/useCreateQuoteRating';
import { useGetQuoteRating } from '@services/quoteRating/useGetQuoteRating';
import { useSearchQuoteRatings } from '@services/quoteRating/useSearchQuoteRatings';
import { QuoteRating, quoteRatingStatus } from '@models/quoteRating';

/**
 * Kicks off a quote rating job and polls it to completion, reporting progress via `setIsRating`.
 * Also picks up any rating job that was already pending/in-progress when the panel mounted.
 */
export const useQuoteRatingJob = ({
  quoteId,
  setIsRating,
}: {
  quoteId: string;
  setIsRating: (isRating: boolean) => void;
}) => {
  const { mutateAsync: rateQuote, isPending: isCreateRatingLoading } = useCreateQuoteRating();

  const [activeRating, setActiveRating] = useState<{
    isActive: boolean;
    id: string | undefined;
  }>({ isActive: false, id: undefined });

  const { data: searchQuoteRatingsData } = useSearchQuoteRatings({
    quoteId,
    enabled: !isCreateRatingLoading,
    params: { pageLimit: 10 },
  });
  const searchQuoteRatings = searchQuoteRatingsData?.pages.flatMap<QuoteRating>((page) => page.items);
  const activeRatingJob = searchQuoteRatings?.find(
    (rating) => rating.status === quoteRatingStatus.Pending || rating.status === quoteRatingStatus.InProgress,
  );
  const { data: activeQuoteRatingJob } = useGetQuoteRating(quoteId, activeRating.id);

  useEffect(() => {
    switch (activeQuoteRatingJob?.status) {
      case quoteRatingStatus.Succeeded:
        setActiveRating({ isActive: false, id: undefined });
        setIsRating(false);
        break;

      case quoteRatingStatus.Failed: {
        setActiveRating({ isActive: false, id: undefined });
        setIsRating(false);
        enqueueSnackbar('Oops! Rating failed. Please try again.', {
          variant: ToastType.Error,
        });
        break;
      }
      case quoteRatingStatus.InProgress:
      case quoteRatingStatus.Pending:
        setActiveRating((prev) => ({ ...prev, isActive: true }));
        break;
      default:
        break;
    }
  }, [activeQuoteRatingJob, setIsRating]);

  useEffect(() => {
    if (activeRatingJob) {
      setActiveRating({ isActive: true, id: activeRatingJob.id });
    }
  }, [activeRatingJob]);

  const startRating = () =>
    rateQuote({ quoteId }).then((response) => {
      setActiveRating((prev) => ({ ...prev, id: response.id }));
      setIsRating(true);
      return response;
    });

  return { startRating, isCreateRatingLoading };
};
